import { useState, useRef, useEffect } from "react";
import { svgPaths, chatSvg } from "./svgPaths";
import imgBackground from "./assets/chat-background.png";
import AsyncAvatar from "../../common/async-avatar/async-avatar/AsyncAvatar";
import { getAccount } from "../../common/services/commonService";
import "./shared.css";

export { svgPaths, chatSvg };

// ─── Shared: User Avatar (logged-in user photo + status dot) ───────────────────
// Uses the signed-in MSAL account to render the same avatar as the app Header
// (Graph photo with initials fallback), overlaid with the design-system status dot.

export function UserAvatar({ size = 32 }) {
  const dot = Math.max(8, Math.round(size / 4));
  const account = getAccount();
  return (
    <div className="dbs-chatbot-avatar" style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <div style={{ width: size, height: size, borderRadius: 200, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <AsyncAvatar id={account?.localAccountId} name={account?.name} size="small" />
      </div>
      <div style={{ position: "absolute", bottom: 0, right: 0, width: dot, height: dot, borderRadius: 200, backgroundColor: "var(--eyp-color-green-600, #168736)", border: "1px solid var(--bg-colors-default-extra-a, #f4f7fc)", boxSizing: "border-box" }} />
    </div>
  );
}

// ─── Shared: Page Background ──────────────────────────────────────────────────

export function PageBackground() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <img src={imgBackground} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    </div>
  );
}

// ─── Shared: AI Sparkle Avatar ────────────────────────────────────────────────

export function AISparkle({ size = 24 }) {
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg style={{ position: "absolute", left: "calc(50% - 2.4px)", top: "20%", bottom: "5%", width: "75%", aspectRatio: "217/214" }} fill="none" viewBox="0 0 24 24" preserveAspectRatio="none">
        <path d={chatSvg.p622e380} fill="url(#ai-grad-a)" />
        <defs>
          <linearGradient id="ai-grad-a" gradientUnits="userSpaceOnUse" x1="0" x2="24.28" y1="24" y2="23.72">
            <stop stopColor="#1A9AFA" /><stop offset="0.92" stopColor="#7815EF" /><stop offset="1" stopColor="#FF32FF" />
          </linearGradient>
        </defs>
      </svg>
      <svg style={{ position: "absolute", left: "calc(50% + 8px)", top: "5%", bottom: "55%", width: "40%", aspectRatio: "217/214" }} fill="none" viewBox="0 0 12.9794 12.8" preserveAspectRatio="none">
        <path d={chatSvg.p4e91f00} fill="url(#ai-grad-b)" />
        <defs>
          <linearGradient id="ai-grad-b" gradientUnits="userSpaceOnUse" x1="0" x2="13.13" y1="12.8" y2="12.64">
            <stop stopColor="#1A9AFA" /><stop offset="0.92" stopColor="#7815EF" /><stop offset="1" stopColor="#FF32FF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Small sparkle used inside the Deep Research button
function DeepResearchSparkle() {
  return (
    <div style={{ position: "relative", width: 16, height: 16, flexShrink: 0 }}>
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} fill="none" viewBox="0 0 24 24" preserveAspectRatio="none">
        <path d={chatSvg.p622e380} fill="url(#dr-grad-a)" />
        <path d={chatSvg.p4e91f00} fill="url(#dr-grad-b)" transform="translate(11 1) scale(0.5)" />
        <defs>
          <linearGradient id="dr-grad-a" gradientUnits="userSpaceOnUse" x1="0" x2="24.28" y1="24" y2="23.72">
            <stop stopColor="#1A9AFA" /><stop offset="0.92" stopColor="#7815EF" /><stop offset="1" stopColor="#FF32FF" />
          </linearGradient>
          <linearGradient id="dr-grad-b" gradientUnits="userSpaceOnUse" x1="0" x2="13.13" y1="12.8" y2="12.64">
            <stop stopColor="#1A9AFA" /><stop offset="0.92" stopColor="#7815EF" /><stop offset="1" stopColor="#FF32FF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// ─── Shared: Chat Input (EYP AI input composer) ───────────────────────────────

function SendIcon() {
  return <div style={{ position: "relative", width: 15, height: 15 }}><svg style={{ position: "absolute", inset: "13%", width: "85%", height: "73%" }} fill="none" viewBox="0 0 17.0833 14.5834" preserveAspectRatio="none"><path clipRule="evenodd" d={svgPaths.p2d8db900} fill="#ffffff" fillRule="evenodd" /></svg></div>;
}

function StopIcon() {
  return <div style={{ position: "relative", width: 15, height: 15 }}><svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} fill="none" viewBox="0 0 20 20" preserveAspectRatio="none"><rect x="5" y="5" width="10" height="10" rx="2" fill="#ffffff" /></svg></div>;
}

function ChevronDown() {
  return (
    <div style={{ position: "relative", width: 16, height: 16, flexShrink: 0 }}>
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} fill="none" viewBox="0 0 16 16" preserveAspectRatio="none">
        <path d="M4 6L8 10L12 6" stroke="var(--icon-colors-default-primary, #064372)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

const RESEARCH_MODES = ["Deep Research", "Quick Responses", "Balanced"];

export function ChatInput({ value, onChange, onSubmit, onStop, appState, mode: modeProp, onModeChange }) {
  const isLoading = appState === "loading";
  const [modeInternal, setModeInternal] = useState("Deep Research");
  const mode = modeProp ?? modeInternal;
  const setMode = (m) => { onModeChange ? onModeChange(m) : setModeInternal(m); };
  const [modeOpen, setModeOpen] = useState(false);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isLoading && value.trim()) {
      e.preventDefault();
      onSubmit();
    }
  };

  const hasText = !!value.trim();
  const [sendHover, setSendHover] = useState(false);
  const [sendPressed, setSendPressed] = useState(false);
  const [modeHover, setModeHover] = useState(false);
  const [modePressed, setModePressed] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [value]);

  const gradient = "linear-gradient(90deg, #1A9AFA 0%, #7815EF 60%, #FF32FF 100%)";

  return (
    <div style={{ borderRadius: "var(--border-radius-sm, 4px)", border: "1.5px solid transparent", background: `linear-gradient(#ffffff, #ffffff) padding-box, ${gradient} border-box`, width: "100%", display: "flex", flexDirection: "column", flexShrink: 0, position: "relative", zIndex: 2, maxHeight: "25vh", overflow: "hidden" }}>
      <div style={{ flex: "1 0 0", display: "flex", padding: "var(--spacing-8, 8px) var(--spacing-12, 12px)", overflowY: "auto", minHeight: 58 }}>
        <textarea
          ref={textareaRef}
          value={isLoading ? "" : value}
          onChange={(e) => !isLoading && onChange(e.target.value)}
          onKeyDown={handleKey}
          placeholder={isLoading ? "Generating response..." : "Ask anything..."}
          disabled={isLoading}
          rows={2}
          aria-label="Chat message"
          style={{ width: "100%", border: "none", outline: "none", resize: "none", background: "transparent", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "1.5", color: hasText && !isLoading ? "var(--text-colors-default-primary, #2e2e38)" : "var(--text-colors-default-secondary, #656579)", cursor: isLoading ? "default" : "text", overflow: "hidden" }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "var(--spacing-8, 8px) var(--spacing-12, 12px)" }}>
        {/* Deep Research mode selector */}
        {/* <div style={{ position: "relative", flexShrink: 0 }}>
          {modeOpen && (
            <>
              <div onClick={() => setModeOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
              <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, minWidth: 200, backgroundColor: "var(--bg-colors-default-primary, #ffffff)", border: "1px solid var(--border-colors-default-primary, #d8e5fd)", borderRadius: "var(--border-radius-sm, 4px)", boxShadow: "0px 4px 16px color-mix(in srgb, var(--shadow-colors-primary, #155cb4) 14%, transparent)", padding: "var(--spacing-4, 4px)", display: "flex", flexDirection: "column", zIndex: 40 }}>
                {RESEARCH_MODES.map((m) => {
                  const active = m === mode;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setMode(m); setModeOpen(false); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--spacing-8, 8px)", padding: "var(--spacing-8, 8px) var(--spacing-12, 12px)", borderRadius: "var(--border-radius-sm, 4px)", border: "none", backgroundColor: active ? "var(--bg-colors-hover-secondary, #d8e5fd)" : "transparent", cursor: "pointer", textAlign: "left", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", color: active ? "var(--text-colors-selected-default-primary, #2e2e38)" : "var(--text-colors-default-primary, #2e2e38)", whiteSpace: "nowrap", transition: "background-color 0.15s ease" }}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = "var(--bg-colors-hover-secondary, #d8e5fd)"; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                    >
                      {m}
                      {active && (
                        <svg style={{ width: 20, height: 20, flexShrink: 0 }} fill="none" viewBox="4 4.5 10 9" preserveAspectRatio="xMidYMid meet">
                          <path clipRule="evenodd" d="M4.34972 8.93306C4.5938 8.68898 4.98953 8.68898 5.23361 8.93306L7.29167 10.9911L12.6831 5.59973C12.9271 5.35565 13.3229 5.35565 13.5669 5.59973C13.811 5.8438 13.811 6.23953 13.5669 6.48361L7.73361 12.3169C7.48953 12.561 7.0938 12.561 6.84973 12.3169L4.34972 9.81694C4.10565 9.57286 4.10565 9.17714 4.34972 8.93306Z" fill="var(--icon-colors-selected-default-primary, #064372)" fillRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <button
            type="button"
            disabled={isLoading}
            onClick={() => setModeOpen((o) => !o)}
            onMouseEnter={() => setModeHover(true)}
            onMouseLeave={() => { setModeHover(false); setModePressed(false); }}
            onMouseDown={() => setModePressed(true)}
            onMouseUp={() => setModePressed(false)}
            style={{ display: "flex", alignItems: "center", gap: "var(--spacing-8, 8px)", height: 32, padding: "0 var(--spacing-12, 12px)", borderRadius: "var(--border-radius-sm, 4px)", border: "1px solid var(--border-colors-default-secondary, #afcdfb)", backgroundColor: modePressed ? SECONDARY_COLORS.activeBg : modeHover ? SECONDARY_COLORS.hoverBg : "transparent", cursor: isLoading ? "default" : "pointer", flexShrink: 0, opacity: isLoading ? 0.5 : 1, transition: "background-color 0.15s ease, border-color 0.15s ease" }}
          >
            <DeepResearchSparkle />
            <span style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", whiteSpace: "nowrap" }}>{mode}</span>
            <div style={{ transform: modeOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}><ChevronDown /></div>
          </button>
        </div> */}
        {/* Circular send / stop icon button */}
        <button
          onClick={isLoading ? onStop : onSubmit}
          disabled={!isLoading && !hasText}
          aria-label={isLoading ? "Stop generating" : "Send message"}
          onMouseEnter={() => setSendHover(true)}
          onMouseLeave={() => { setSendHover(false); setSendPressed(false); }}
          onMouseDown={() => setSendPressed(true)}
          onMouseUp={() => setSendPressed(false)}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", cursor: isLoading || hasText ? "pointer" : "default", backgroundColor: (isLoading || hasText) ? (sendPressed ? PRIMARY_COLORS.active : sendHover ? PRIMARY_COLORS.hover : PRIMARY_COLORS.default) : "var(--border-colors-default-secondary, #afcdfb)", border: "none", flexShrink: 0, transition: "background-color 0.15s ease" }}
        >
          {isLoading ? <StopIcon /> : <SendIcon />}
        </button>
      </div>
    </div>
  );
}

// ─── Shared: Composer + disclaimer block ──────────────────────────────────────

export function Composer({ value, onChange, onSubmit, onStop, appState, mode, onModeChange }) {
  return (
    <div style={{ width: "100%", flexShrink: 0, position: "relative", zIndex: 1 }}>
      <ChatInput value={value} onChange={onChange} onSubmit={onSubmit} onStop={onStop} appState={appState} mode={mode} onModeChange={onModeChange} />
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--spacing-4, 4px)", paddingTop: "var(--spacing-4, 4px)" }}>
        <p style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", lineHeight: "1.5", color: "var(--text-colors-default-secondary, #656579)", margin: 0 }}>AI generated output may contain inaccuracies or errors. Verify output before use.</p>
        
      </div>
    </div>
  );
}

// ─── Shared: Design-system Buttons (Primary / Secondary) ───────────────────────

export const PRIMARY_COLORS = {
  default: "var(--eyp-color-blue-900-dark-blue, #072e65)",
  hover: "#064372",
  active: "#155cb4",
  disabledBg: "#afaeba",
  text: "#ffffff",
  disabledText: "#f3f3f5",
};

export const SECONDARY_COLORS = {
  border: "var(--accent-info-primary, #0f69ae)",
  text: "var(--eyp-color-blue-900-dark-blue, #072e65)",
  hoverBg: "#d8e5fd",
  hoverBorder: "#afcdfb",
  activeBg: "#afcdfb",
  activeBorder: "#afcdfb",
  disabledBorder: "#9897a6",
  disabledText: "#656579",
};

export function PrimaryButton({ children, onClick, disabled = false, type = "button", ariaLabel, style }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = disabled ? PRIMARY_COLORS.disabledBg : pressed ? PRIMARY_COLORS.active : hover ? PRIMARY_COLORS.hover : PRIMARY_COLORS.default;
  const color = disabled ? PRIMARY_COLORS.disabledText : PRIMARY_COLORS.text;
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--spacing-8, 8px)", padding: "0 var(--spacing-16, 16px)", height: 40, borderRadius: "var(--border-radius-sm, 4px)", backgroundColor: bg, color, border: "none", cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0, fontSize: "var(--font-size-14, 0.875rem)", whiteSpace: "nowrap", transition: "background-color 0.15s ease", ...style }}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, disabled = false, type = "button", ariaLabel, style, staticBorder }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = disabled ? "transparent" : pressed ? SECONDARY_COLORS.activeBg : hover ? SECONDARY_COLORS.hoverBg : "transparent";
  const border = disabled ? SECONDARY_COLORS.disabledBorder : staticBorder ? staticBorder : pressed ? SECONDARY_COLORS.activeBorder : hover ? SECONDARY_COLORS.hoverBorder : SECONDARY_COLORS.border;
  const color = disabled ? SECONDARY_COLORS.disabledText : SECONDARY_COLORS.text;
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--spacing-8, 8px)", padding: "0 var(--spacing-16, 16px)", height: 40, borderRadius: "var(--border-radius-sm, 4px)", backgroundColor: bg, color, border: `1px solid ${border}`, cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0, fontSize: "var(--font-size-14, 0.875rem)", whiteSpace: "nowrap", transition: "background-color 0.15s ease, border-color 0.15s ease", ...style }}
    >
      {children}
    </button>
  );
}

// ─── Shared: New Chat button (design-system primary button) ────────────────────

export function NewChatButton({ onClick }) {
  return (
    <PrimaryButton onClick={onClick} ariaLabel="New Chat" style={{ fontSize: "var(--font-size-16, 1rem)" }}>
      <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}>
        <svg style={{ position: "absolute", inset: "22%", width: "56%", height: "56%" }} fill="none" viewBox="0 0 15 15" preserveAspectRatio="none">
          <path clipRule="evenodd" d={chatSvg.p80fe780} fill="#ffffff" fillRule="evenodd" />
        </svg>
      </div>
      <span style={{ whiteSpace: "nowrap" }}>New Chat</span>
    </PrimaryButton>
  );
}

// ─── Shared: Footer ───────────────────────────────────────────────────────────

const PRIVACY_NOTICE_PATH = "/chat/privacy-notice";

export function Footer() {
  const getEnvValue = (name) => {
    if (typeof process !== "undefined" && process.env && name in process.env) {
      return process.env[name];
    }
    return "";
  };

  const footerActions = [
    { label: "Terms of Use", href: getEnvValue("REACT_APP_ONE_EDGE_TERMSURL") || "" },
    { label: "Privacy Notice", href: PRIVACY_NOTICE_PATH },
    { label: "Engagement Management Guidance", href: getEnvValue("REACT_APP_ONE_EDGE_EMGURL") || "" },
    { label: "AI Disclaimer", href: getEnvValue("REACT_APP_ONE_EDGE_AI_DISCLAIMERURL") || "" },
  ];

  return (
    <div className="footer-content">
      <div className="footer-main-content">
        <p className="footer-text">
          Copyright © 2026 EYGM Limited. All rights reserved. Do not enter
          personally identifiable information into this site.
        </p>
        <div className="footer-divider" />
      </div>
      <div className="footer-actions">
        {footerActions.map(({ label, href }) => (
          <a
            className="footer-action"
            href={typeof href === "function" ? href() : href}
            key={label}
            rel="noreferrer"
            target="_blank"
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}